import { useEffect, useRef } from 'react';
import { getMe } from '@/lib/api/auth';
import { HANDOFF_MESSAGE_TYPE, type HandoffMessage } from '@/lib/auth/reauth';

/**
 * The popup's last stop, on the workspace origin.
 *
 * By the time this renders, compute-api's SPA handler has already exchanged the
 * ?code= and set the session cookie — on this origin, which the opener shares.
 * All that remains is to tell the opener and get out of the way.
 *
 * The session is probed rather than assumed: the SPA handler logs a failed code
 * exchange and serves the page anyway, so arriving here is not by itself proof
 * that a session exists.
 *
 * Rendered ahead of RootLayout. Inside it, this page would mount the whole
 * provider tree, block on /me in AppShell, and trip RootLayout's own
 * redirect-to-login inside the popup.
 */
export function HandoffCompletePage() {
  const reportedRef = useRef(false);

  useEffect(() => {
    if (reportedRef.current) return;
    reportedRef.current = true;

    const nonce = new URLSearchParams(window.location.search).get('nonce') ?? '';

    const report = (status: HandoffMessage['status']) => {
      const opener = window.opener as Window | null;
      if (opener) {
        const message: HandoffMessage = { type: HANDOFF_MESSAGE_TYPE, status, nonce };
        // Explicit target origin, never '*': the message says a session exists,
        // and only the tab that asked has any business knowing.
        opener.postMessage(message, window.location.origin);
      }
      window.close();
    };

    getMe()
      .then((me) => report(me?.authenticated ? 'ok' : 'needs-login'))
      .catch(() => report('needs-login'));
  }, []);

  return (
    <div
      style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}
    >
      <p>Signing you in…</p>
    </div>
  );
}

export default HandoffCompletePage;
